<template>
  <component
    :is="tag"
    class="not-row-reverse"
    @focusin="focusHandler"
    @submit.prevent="formSubmit"
  >
    <div v-for="(num, index) in count" :key="`digit_${num}`">
      <input
        v-model.number="digits[index]"
        v-validate="'required|numeric|max:1'"
        class=" text-center number-input-indo"
        :class="{
          'is-invalid': errors.first(`digit-${num}`),
          'is-focused': digits[index],
        }"
        :name="`digit-${num}`"
        type="tel"
        maxlength="1"
        autocomplete="off"
        :tabindex="`${num}`"
        placeholder="-"
        :disabled="disabled"
        @input="event => inputNumber(num, event)"
        @keydown="event => checkInput(num, event)"
        @keydown.left="() => focusToInput(num - 1)"
        @keydown.right="() => focusToInput(num + 1)"
        @keydown.enter="formSubmit"
        @paste="pasteCode"
      />
    </div>
  </component>
</template>

<script>
import Vue from 'vue';
import { prop } from '@/utils/factories';

export default {
  name: 'InputVerify',
  inject: ['$validator'],

  props: {
    value: prop(String),
    tag: prop(String, 'div'),
    count: prop(Number, 4),
    disabled: prop(Boolean),
    autoSend: prop(Boolean),
    autofocus: prop(Boolean, true),
  },
  data() {
    return {
      digits: [],
    };
  },
  computed: {
    code() {
      const code = this.digits.join('');
      return code.length === this.count ? code : null;
    },
    errorText() {
      let err;
      for (let i = 1; i <= this.count; i++) {
        err = this.errors.first(`digit-${i}`);
        if (err) {
          return err;
        }
      }

      return null;
    },
  },
  watch: {
    value(str) {
      if (str !== this.code) {
        this.digits = str && this.$lodash.isString(str) ? [...str] : [];
      }
    },
    errorText(err) {
      this.$emit('error', err);
    },
  },
  mounted() {
    if (this.autofocus) {
      this.focusToInput(1);
    }
  },
  methods: {
    formSubmit() {
      this.$emit('onSubmit', this.code);
    },
    focusToInput(toIndex) {
      const nextFocusElement = this.$el.querySelector(
        `[tabindex="${toIndex}"]`
      );

      if (!nextFocusElement) {
        if (this.autoSend) {
          this.formSubmit();
        }
        return;
      }

      nextFocusElement.focus();
    },
    inputNumber(currentTabIndex, { data }) {
      this.errorRequest = null;
      if (data && !Number.isNaN(+data)) {
        this.focusToInput(currentTabIndex + 1);
      }
      this.triggerInput();
    },
    checkInput(currentTabIndex, event) {
      const { key, ctrlKey, metaKey } = event;
      const isCtrlV = (ctrlKey || metaKey) && key === 'v';
      if (key.length === 1 && Number.isNaN(+key) && !isCtrlV) {
        event.preventDefault();
      } else if (key === 'Backspace') {
        if (!event.target.value) {
          this.focusToInput(currentTabIndex - 1);
        }
      }
    },
    focusHandler(e) {
      e.target.select();
    },
    triggerInput() {
      if (this.code) {
        this.$emit('input', this.code);
      }
    },
    pasteCode(ev) {
      ev.preventDefault();

      let paste = ev.clipboardData.getData('text') || '';
      paste = paste.replace(/\D/g, '');
      if (paste.length !== this.count) {
        return;
      }
      for (let i = 0; i < this.count; i++) {
        Vue.set(this.digits, i, parseInt(paste[i]));
      }
      this.focusToInput(this.digits.length);
      this.$validator.reset();
      this.triggerInput();
      this.$nextTick(() => {
        if (this.autoSend) {
          this.formSubmit();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.number-wrapper {
  font-family: 'Alexandria', sans-serif !important;
  > div {
    > .number-input-indo {
      max-width: 134px;

      background: rgba(0, 0, 0, 0.56);
      backdrop-filter: blur(4px);
      display: flex;
      height: 134px;
      padding: 43px 23px 43px 24px;
      justify-content: center;
      align-items: center;
      flex: 1 0 0;
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.06);
      background: rgba(255, 255, 255, 0.02);
      box-shadow: 0px 2.03044px 12.18264px 0px rgba(255, 255, 255, 0.1) inset;
      backdrop-filter: blur(4px);
      color: #fff;
      text-align: center;

      font-family: Inter;
      font-size: 40px;
      font-style: normal;
      font-weight: 500;
      line-height: 120%; /* 48px */
      letter-spacing: -0.09px;
      @media (max-width: 900px) {
        font-size: 28px;
        max-width: 65px;
        max-height: 65px;
      }
      &.is-focused {
        border-radius: 4px;
        border: 1px solid var(--secondary-white-white-50, #fff);
        background: rgba(255, 255, 255, 0.02);
        box-shadow: 0px 2.03044px 12.18264px 0px rgba(255, 255, 255, 0.1) inset;
        backdrop-filter: blur(4px);
      }
      &.is-invalid {
        border-color: $ph-error;
        padding-right: 0 !important;
        background-position: center center;
        &::placeholder {
          color: transparent;
        }
      }
    }
  }
}
</style>
